/**
 * Recruit hero move for the Legendary Arena game engine.
 *
 * recruitHero removes a hero from an HQ slot and places it in the current
 * player's discard pile. Follows the three-step validation contract:
 * validate args, check stage gate, mutate G.
 *
 * This is a non-core move that gates internally (same pattern as
 * revealVillainCard from WP-014A). It is NOT added to CoreMoveName,
 * CORE_MOVE_NAMES, or MOVE_ALLOWED_STAGES.
 *
 * No registry imports. No .reduce(). Moves never throw.
 */
import { addResources, getAvailableRecruit, recordRecruitedHeroCost, spendRecruit } from '../economy/economy.logic.js';
import { resolveRecruitCost } from '../hero/sizeChanging.logic.js';
import { matchHasRecruitedHeroRitual } from './ritualArtifact.js';
import { refillHqSlot } from '../board/city.logic.js';
import { isHqSlotHaunted } from '../board/haunt.logic.js';
import { hasPendingKoHeroChoice } from './koHeroChoice.resolve.js';
import { hasPendingScryKoChoice } from './scryKoChoice.resolve.js';
import { hasPendingMelterKoChoice } from './melterKoChoice.resolve.js';
import { hasPendingRuthlessDictatorChoice } from './ruthlessDictatorChoice.resolve.js';
import { hasPendingElectromagneticBubbleChoice } from './electromagneticBubbleChoice.resolve.js';
import { hasPendingDiscardChoice } from './discardChoice.resolve.js';
import { hasPendingPutCardsOnDeckChoice } from './putCardsOnDeckChoice.resolve.js';
import { hasPendingKoDiscardChoice } from './koDiscardChoice.resolve.js';
import { hasPendingReorderChoice } from './reorderChoice.resolve.js';
import { hasPendingDefeatChoice } from './defeatChoice.resolve.js';
import { hasPendingOptionalKoReward } from './optionalKoReward.resolve.js';
import { hasPendingSmashDiscard } from './smashDiscard.resolve.js';
import { hasPendingPutHandOnDeckTop } from './putHandOnDeckTop.resolve.js';
import { hasPendingRevealTopDispose } from './revealTopDispose.resolve.js';
import { hasPendingRevealThreeAssign } from './revealThreeAssign.resolve.js';
import { hasPendingDoOver } from './doOver.resolve.js';
import { hasPendingPlayVillainTopChoice } from './playVillainTop.resolve.js';
import { hasPendingVictoryPileCardPick } from './resolveVictoryPileCardPick.js';
import { hasPendingDrawOrEmpowered } from './drawOrEmpowered.resolve.js';
import { hasPendingCoveringFireChoice } from './coveringFireChoice.resolve.js';
import { hasPendingMoveVillainChoice } from './moveVillainChoice.resolve.js';
import { hasPendingSplitFaceChoice } from './splitFaceChoice.resolve.js';
import { hasPendingCountScaledChoice } from './countScaledChoice.resolve.js';
import { hasPendingUndercoverChoice } from './undercover.resolve.js';
import { hasPendingReturnZeroCostDiscard } from './resolveReturnZeroCostDiscard.js';
import { hasPendingDiscardToPlay } from './resolveDiscardToPlay.js';
import { hasPendingReturnOnDiscard } from './resolveReturnOnDiscard.js';
import { hasPendingGiveHqHeroChoice } from './giveHqHeroChoice.resolve.js';
import { hasPendingCopyPowersChoice } from './copyPowersChoice.resolve.js';
import { hasPendingSeatChoice } from './seatChoice.resolve.js';
import { hasHealedThisTurn } from './healWounds.js';
import { getHooksForCard, filterHooksByTiming } from '../rules/heroAbility.types.js';
import { formatCardRef } from '../log/logDisplay.js';
import { pushLog } from '../log/logPush.js';
/**
 * Recruits a hero from the HQ.
 *
 * Removes the card from the specified HQ slot and places it in the
 * current player's discard pile.
 *
 * @param context - boardgame.io move context with G, ctx.
 * @param args - The HQ slot index to recruit from.
 */
export function recruitHero({ G, ctx }, { hqIndex, toTopOfDeck }) {
    // Step 1: Validate args
    if (typeof hqIndex !== 'number' ||
        !Number.isFinite(hqIndex) ||
        !Number.isInteger(hqIndex) ||
        hqIndex < 0 ||
        hqIndex > 4) {
        return;
    }
    const cardId = G.hq[hqIndex];
    if (cardId === null || cardId === undefined) {
        return;
    }
    // why: WP-757 / D-24587 — rulebook v23 p.27: players can't recruit a Haunted Hero
    // while its haunter is under it (exorcise it instead). Silent no-op before any spend.
    if (isHqSlotHaunted(G, hqIndex)) {
        return;
    }
    // why: silent failure preserves deterministic move contract — insufficient
    // recruit points means the recruit cannot proceed.
    // why: D-24801 — the cost paid is the Size-Changing-reduced cost (rules v23 p.33 / p.30),
    // read from the recruiting player's cards played this turn. Microscopic Size-Changing can
    // take it below 0: the player pays nothing and gains the difference as Recruit (p.30).
    const printedCost = G.cardStats[cardId]?.cost ?? 0;
    const recruitCost = resolveRecruitCost(G, ctx.currentPlayer, cardId);
    const requiredCost = Math.max(0, recruitCost);
    const availableRecruit = getAvailableRecruit(G.turnEconomy);
    if (availableRecruit < requiredCost) {
        return;
    }
    // Step 2: Stage gate (non-core move, internal gating)
    // why: recruiting happens during the main action window; non-core moves
    // gate internally per the WP-014A precedent
    if (G.currentStage !== 'main')
        return;
    // why: block-all guard (D-24008) — while a KO-a-Hero choice is pending the
    // board is frozen; recruitHero returns with no side effects. Placed
    // immediately after the stage gate, before any G/zone write.
    if (hasPendingKoHeroChoice(G))
        return;
    // why: block-all guard (D-24282) — a pending Doombot scry-KO choice freezes the
    // board until the player picks which revealed card to KO.
    if (hasPendingScryKoChoice(G))
        return;
    // why: WP-603 / D-24413 — block-all guard: a pending Melter Fight KO/keep choice
    // freezes the board until the fighting player resolves every revealed deck top.
    if (hasPendingMelterKoChoice(G))
        return;
    // why: WP-695 / D-24512 — block-all guards for the two interactive core mastermind
    // tactics (Ruthless Dictator scry-3 / Electromagnetic Bubble X-Men pick).
    if (hasPendingRuthlessDictatorChoice(G))
        return;
    if (hasPendingElectromagneticBubbleChoice(G))
        return;
    // why: block-all guard (WP-476 / D-24284) — a pending discard-to-limit choice
    // freezes the board until the current player picks which cards to discard.
    if (hasPendingDiscardChoice(G))
        return;
    if (hasPendingPutCardsOnDeckChoice(G))
        return;
    if (hasPendingReorderChoice(G))
        return; // why: WP-479 / D-24286 block-all guard
    if (hasPendingDefeatChoice(G))
        return; // why: WP-486 / D-24291 block-all guard
    if (hasPendingKoDiscardChoice(G))
        return; // why: WP-693 / D-24510 block-all guard
    // why: block-all guard (D-24019) — optional-KO-reward choice pending; the
    // board is frozen until resolved (beside the D-24008 KO-hero check above).
    if (hasPendingPlayVillainTopChoice(G))
        return; // why: WP-663 / D-24474 — block-all guard (Shadowed Thoughts play-villain-top choice)
    if (hasPendingOptionalKoReward(G))
        return;
    if (hasPendingSmashDiscard(G))
        return; // why: WP-676 / D-24492 — block-all guard (Smash discard-for-attack choice)
    if (hasPendingPutHandOnDeckTop(G))
        return; // why: WP-700 / D-24519 — block-all guard (put-a-hand-card-on-deck-top choice)
    if (hasPendingRevealTopDispose(G))
        return; // why: WP-702 / D-24521 — block-all guard (reveal-top discard-or-keep choice)
    if (hasPendingRevealThreeAssign(G))
        return; // why: WP-753 / D-24580 — block-all guard (reveal-three draw / discard / KO assignment)
    if (hasPendingDoOver(G))
        return; // why: WP-681 / D-24498 — block-all guard (Do-Over accept/decline choice)
    // why: block-all — pendingVictoryPileCardPick must be resolved before any other action (D-24067)
    if (hasPendingVictoryPileCardPick(G))
        return;
    // why: block-all — pendingDrawOrEmpowered must be resolved before any other action (D-24069)
    if (hasPendingDrawOrEmpowered(G))
        return;
    // why: block-all guard (WP-719 / D-24541) — a pending Covering Fire choice freezes the board.
    if (hasPendingCoveringFireChoice(G))
        return;
    // why: block-all guard (WP-795 / D-24664) — a pending Spinning Cyclone move-a-Villain choice freezes the board.
    if (hasPendingMoveVillainChoice(G))
        return;
    // why: block-all guard (WP-724 / D-24546) — a pending split-face "choose a side" freezes the board.
    if (hasPendingSplitFaceChoice(G))
        return;
    if (hasPendingCountScaledChoice(G))
        return;
    // why: block-all — pendingUndercoverChoice must be resolved before any other action (WP-678 / D-24494)
    if (hasPendingUndercoverChoice(G))
        return;
    // why: block-all — pendingReturnZeroCostDiscard must be resolved before any other action (D-24139)
    if (hasPendingReturnZeroCostDiscard(G))
        return;
    // why: block-all — pendingDiscardToPlay must be resolved before any other action (WP-383 / D-24184)
    if (hasPendingDiscardToPlay(G))
        return;
    // why: block-all — pendingReturnOnDiscard must be resolved before any other action (WP-498 / D-24301)
    if (hasPendingReturnOnDiscard(G))
        return;
    // why: block-all — pendingGiveHqHeroChoice (Paibok Fight) must be resolved first (WP-532 / D-24343)
    if (hasPendingGiveHqHeroChoice(G))
        return;
    // why: block-all — pendingCopyPowersChoice (Rogue's Copy Powers) must be resolved first (WP-535 / D-24345)
    if (hasPendingCopyPowersChoice(G))
        return;
    if (hasPendingSeatChoice(G))
        return; // why: WP-684 / D-24501 — block-all (non-active/multi-seat pending choice)
    // why: D-24180 — a player who used the Wound Healing ability this turn may not
    // fight or recruit for the rest of the turn (the reverse lock).
    if (hasHealedThisTurn(G))
        return;
    // Step 3: Mutate G
    // why: D-24049 — the printed "Wall-Crawl" ability ("when you recruit this Hero,
    // you may put it on top of your deck") is optional and acts on the recruiting
    // player's OWN deck via their own recruit action — no hidden information, no
    // opponent interaction — so it needs no pending-choice/board-freeze guard. The
    // hook query is read-only: getHooksForCard is 2-arg (no timing param), so the
    // onRecruit wall-crawl hook is reached by filtering its result to onRecruit and
    // checking the keyword specifically (never "the first onRecruit hook"). The
    // Array.isArray guard covers narrow test mocks that omit G.heroAbilityHooks.
    const placeOnDeckTop = toTopOfDeck === true &&
        Array.isArray(G.heroAbilityHooks) &&
        filterHooksByTiming(getHooksForCard(G.heroAbilityHooks, cardId), 'onRecruit').some((hook) => hook.keywords.includes('wall-crawl'));
    // why: D-24049 — deck[0] is the next-draw position (drawFromPlayerDeck draws
    // deck[0]), so the deck-top placement uses unshift. WP-018 — economy deduction
    // lands after the placement; WP-135 — HQ slot refill lands after that. The slot
    // is vacated by refillHqSlot (which assigns null when heroDeck is empty per
    // D-13503), so we must not pre-null G.hq[hqIndex] here. When placeOnDeckTop is
    // false (toTopOfDeck falsy or no wall-crawl onRecruit hook) the discard placement
    // is byte-identical to the pre-WP-273 behavior.
    if (placeOnDeckTop) {
        G.playerZones[ctx.currentPlayer].deck.unshift(cardId);
    }
    else {
        G.playerZones[ctx.currentPlayer].discard.push(cardId);
    }
    G.turnEconomy = spendRecruit(G.turnEconomy, requiredCost);
    // why: D-24801 — rules v23 p.30: "When you recruit a Microscopic Size-Changing Hero with a
    // negative cost, you actually gain that many Recruit points!"
    if (recruitCost < 0) {
        G.turnEconomy = addResources(G.turnEconomy, 0, -recruitCost);
    }
    // why: WP-858 / D-24794 — record the recruited Hero's printed cost for Staff of Legba's Ritual
    // ("If you recruited a Hero … +Attack equal to that Hero's cost"), only in a match whose hooks
    // carry ritual-recruited-hero, so no other match's turnEconomy gains the field (no hash surface).
    // why: D-24801 — the printed cost, not the Size-Changing price: "The Size-Changing card's cost
    // is only changed during the moment you are recruiting it, not other times" (rules v23 p.33).
    if (matchHasRecruitedHeroRitual(G)) {
        G.turnEconomy = recordRecruitedHeroCost(G.turnEconomy, printedCost);
    }
    // why: D-24180 — this successful recruit marks the player as having acted this
    // turn, which bars the Wound Healing ability for the rest of the turn.
    G.hasActedThisTurn = true;
    // why: WP-135 — refill the vacated slot from G.heroDeck (FIFO via shift).
    // Empty-deck case leaves the slot null per D-13503; no auto-reshuffle of
    // recruited cards back into the deck (separate engine WP if ever needed).
    const refillResult = refillHqSlot(G.hq, hqIndex, G.heroDeck);
    G.hq = refillResult.hq;
    G.heroDeck = refillResult.heroDeck;
    // why: WP-135 — log line is replay-visible and snapshotted; format is
    // locked at this site to byte-equality. Replaces the pre-WP-135 line
    // shape from WP-016 (one push per successful recruit, not two). Never
    // add timestamps or non-deterministic context. The empty-deck branch
    // substitutes the trailing parenthetical per the §7.6 byte-locked format.
    const refillSuffix = refillResult.hq[hqIndex] === null
        ? '(heroDeck empty; slot left null)'
        : `(heroDeck.length: ${String(refillResult.heroDeck.length)})`;
    // why: D-24049 — append a Wall-Crawl placement note ONLY on the deck-top branch;
    // the discard branch's line is byte-identical to the pre-WP-273 WP-135 format.
    const placementNote = placeOnDeckTop ? ' (Wall-Crawl: placed on top of deck)' : '';
    // why: D-24801 — a Size-Changing note ONLY when the discount changed the price, so every
    // full-price recruit's line stays byte-identical to the WP-135 format.
    const sizeChangingNote = buildSizeChangingNote(printedCost, recruitCost);
    pushLog(G, `Player ${ctx.currentPlayer} recruited ${formatCardRef(G.cardDisplayData, cardId)}; HQ slot ${String(hqIndex)} refilled from heroDeck ${refillSuffix}${placementNote}${sizeChangingNote}`);
}
/**
 * Builds the recruit log note for a Size-Changing price, or '' when the price is the printed cost.
 *
 * @param printedCost - The Hero's printed cost.
 * @param recruitCost - The Size-Changing-reduced cost; negative when Microscopic went below 0.
 * @returns The note, starting with a space, or an empty string.
 */
function buildSizeChangingNote(printedCost, recruitCost) {
    if (recruitCost === printedCost) {
        return '';
    }
    if (recruitCost < 0) {
        return ` (Size-Changing: cost ${String(printedCost)}, paid 0 and gained ${String(-recruitCost)} recruit)`;
    }
    return ` (Size-Changing: cost ${String(printedCost)}, paid ${String(recruitCost)})`;
}
//# sourceMappingURL=recruitHero.js.map